import { styled } from '@linaria/react';
import { useMemo } from 'react';
import { Area, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../helpers/date-time-helpers/formatting/formatting';
import { coerceToDateInput } from '../../../helpers/date-time-helpers/parsing/parsing';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import { newCOLORS } from '../../../styles/colors';
import { type DeliveryConfidenceChartDataPoint, Metric } from '../work-periods.type';
import { ChartType } from './delivery-confidence.type';

type Props = {
  data: DeliveryConfidenceChartDataPoint[];
  chartType: ChartType;
  handleChartMouseMove: (data: any) => void;
  handleDoneClick: () => void;
};

export const DeliveryConfidenceChart = ({ data, chartType, handleChartMouseMove, handleDoneClick }: Props) => {
  const i18n = useTranslation();
  const { selectedMetric } = useWorkPeriodStore();
  const transformedData = useMemo(() => {
    if (!data.length) return [];
    const totalScope = data[0]?.scope ?? 0;
    const numPoints = Math.max(1, data.length - 1);
    const idealStep = totalScope / numPoints;

    return data.map((dp, index) => {
      if (chartType === ChartType.BURN_DOWN) {
        const idealRemainingValue = Math.max(0, totalScope - index * idealStep);

        const burndownBandValues =
          dp.band && dp.band.length === 2
            ? [Math.max(0, dp.scope - dp.band[1]), Math.max(0, dp.scope - dp.band[0])].sort((a, b) => a - b)
            : [dp.scope, dp.scope];

        const burndownScopeChangeValues =
          dp.scopeChange && dp.scopeChange.length === 2
            ? [Math.max(0, dp.scope - dp.scopeChange[1]), Math.max(0, dp.scope - dp.scopeChange[0])].sort(
                (a, b) => a - b,
              )
            : [dp.scope, dp.scope];

        return {
          ...dp, // Keep original fields for tooltip or other modes
          date: dp.date,
          actualRemaining: Math.max(0, dp.scope - dp.completed),
          idealRemaining: idealRemainingValue,
          // totalScope will use dp.scope directly from original data
          burndownBand: burndownBandValues,
          burndownScopeChangeArea: burndownScopeChangeValues,
        };
      }
      // Goal Attainment mode - ensure necessary fields are present if structure changes significantly
      return {
        ...dp,
        actualRemaining: dp.completed, // For consistency if some part of render uses actualRemaining
        // but Goal Attainment will primarily use 'completed', 'scope', etc.
      };
    });
  }, [data, chartType]);

  return (
    <ChartWrapper>
      <ChartContainer>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={transformedData}
            margin={{ top: 10, right: 30, left: 10, bottom: 0 }}
            onMouseMove={handleChartMouseMove}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="date"
              tickFormatter={(value) => formatDate(value, { format: 'MMM D' })}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{
                value:
                  chartType === ChartType.BURN_DOWN
                    ? selectedMetric === Metric.Tasks
                      ? `${i18n.t('common.work_units')} Remaining`
                      : `${i18n.t('common.estimation')} Remaining`
                    : selectedMetric === Metric.Tasks
                      ? `${i18n.t('common.work_units')} Completed`
                      : `${i18n.t('common.estimation')} Completed`,
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle' },
                offset: 10,
              }}
            />
            <Tooltip
              formatter={(value, name, props) => {
                // Helper function to round values safely
                const roundValue = (val: any) => (typeof val === 'number' ? val.toFixed(1) : val);
                const metricLabel =
                  selectedMetric === Metric.Tasks
                    ? i18n.t('common.work_units').toLowerCase()
                    : i18n.t('common.estimation').toLowerCase();

                // props.payload contains the data point for the tooltip
                const point = props.payload;
                if (chartType === ChartType.BURN_DOWN) {
                  if (name === 'Total Scope') return [`${value} ${metricLabel}`, name];
                  if (name === 'Actual Remaining') return [`${value} ${metricLabel}`, name];
                  if (name === 'Ideal Remaining') return [`${roundValue(value)} ${metricLabel}`, name];
                  if (name === 'Confidence Band (Remaining)') {
                    // burndownBand is [low, high]
                    return [`${point.burndownBand[0]} - ${point.burndownBand[1]} ${metricLabel}`, name];
                  }
                  if (name === 'Scope Change (Remaining)') {
                    // burndownScopeChangeArea is [low, high]
                    return [
                      `${point.burndownScopeChangeArea[0]} - ${point.burndownScopeChangeArea[1]} ${metricLabel}`,
                      name,
                    ];
                  }
                } else {
                  // Goal Attainment
                  if (name === 'Total Scope') return [`${value} ${metricLabel}`, name];
                  if (name === 'Completed') return [`${value} ${metricLabel}`, name];
                  if (name === 'Confidence Band') {
                    // band is [low, high]
                    return [`${point.band[0]} - ${point.band[1]} ${metricLabel}`, name];
                  }
                  if (name === 'Scope Change') {
                    // scopeChange is [low, high]
                    return [`${point.scopeChange[0]} - ${point.scopeChange[1]} ${metricLabel}`, name];
                  }
                }
                return [roundValue(value), name]; // Default return with rounding
              }}
              labelFormatter={(label) => `Date: ${formatDate(coerceToDateInput(label), { format: 'MMM D, YYYY' })}`}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="scope" // Always refers to the total scope of the day
              name="Total Scope"
              stroke={newCOLORS.purple}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey={chartType === ChartType.BURN_DOWN ? 'burndownScopeChangeArea' : 'scopeChange'}
              name={chartType === ChartType.BURN_DOWN ? 'Scope Change (Remaining)' : 'Scope Change'}
              stroke={newCOLORS.lightPurple} // Adjusted stroke color
              strokeWidth={0}
              fill={newCOLORS.lightPurple} // Adjusted fill color
              fillOpacity={0.3}
              isAnimationActive={false}
              connectNulls
            />
            <Area
              type="monotone"
              dataKey={chartType === ChartType.BURN_DOWN ? 'burndownBand' : 'band'}
              name={chartType === ChartType.BURN_DOWN ? 'Confidence Band (Remaining)' : 'Confidence Band'}
              stroke={newCOLORS.lighterBlue}
              strokeWidth={0}
              fill={newCOLORS.lighterBlue}
              fillOpacity={0.3}
              isAnimationActive={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey={chartType === ChartType.BURN_DOWN ? 'actualRemaining' : 'completed'}
              name={chartType === ChartType.BURN_DOWN ? 'Actual Remaining' : 'Completed'}
              stroke={chartType === ChartType.BURN_DOWN ? '#FA5252' : newCOLORS.green}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={(props: any) => {
                const { cx, cy } = props;
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={7}
                    fill={newCOLORS.green}
                    stroke={newCOLORS.green}
                    strokeWidth={2}
                    cursor="pointer"
                    onClick={() => handleDoneClick()}
                  />
                );
              }}
              isAnimationActive={false}
            />
            {chartType === ChartType.BURN_DOWN && (
              <Line
                type="monotone"
                dataKey="idealRemaining"
                name="Ideal Remaining"
                stroke="#4C6EF5" // Blue
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </ChartContainer>
    </ChartWrapper>
  );
};

const ChartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  width: 100%;
`;
